import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { langConversionHelper } from "@/_helpers";

import { TimeLoader } from "@/_components";
import { userLoginSuccess } from "../_redux/actions/accountActions";
import { accountService } from "@/_services";

import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: { margin: theme.spacing(3, 0, 2) },
  buttonIco: { width: 24, height: 24, marginRight: 10, marginTop: 7 },
  inputIco: { opacity: 0.6, color: "#1c355e" },
}));

function Login({ history, location, userLoginSuccess }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const [isPhone, setIsPhone] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [values, setValues] = useState({ showPassword: false });
  // let token =
  //   "aDlnUWhFdHJOYVZxdzAvVmFXRHNDTmpmck9EbW5SRzcycGlpcEk5cEJwME5FL1RVc0VnY0xFd0h4QmowVnpZbDNrUVpFR2Qyb1dVNFYzZ3hEdTc1Zm9kRlpFUzlmTVRP";
  const { register, errors, handleSubmit, trigger, control } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const onSubmit = (data) => {
    setSubmitting(true);
    let phoneData = "";
    if (isPhone && data.email !== null) {
      phoneData = (data.email = data.email.replace(/\D/g, ""));
    } else {
      phoneData = data.email;
    }
    accountService
      .login(phoneData, data.password, isPhone? isPhone : false)
      .then((user) => {
        userLoginSuccess();
        setSubmitting(false);
        let i18nextLocalStorage = localStorage.getItem("i18nextLng");
        if (user.languageID !== undefined) {
          i18n.changeLanguage(
            langConversionHelper.findLanguageAbbrev(user.languageID)
          );
        } else if (i18nextLocalStorage === null) {
          i18n.changeLanguage(langConversionHelper.findLanguageAbbrev(1));
        }
        const { from } = location.state || { from: { pathname: "/" } };
        history.push(from);
      })
      .catch((error) => {
        setSubmitting(false);
        toast.error(error);
      });
  };
  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleInputChange = (event) => {
    let { value } = event.target;
    const hasAt = value.includes("@");
    const hasLetter = /[a-zA-Z]/.test(value || "");
    let isPhoneValue = false;
    if (hasAt || hasLetter) {
      setIsPhone(false);
      isPhoneValue = false;
    } else {
      setIsPhone(true);
      isPhoneValue = true;
    }
    if (countryCode === "USA" && isPhoneValue) {
      const numericValue = value.replace(/[^\d]/g, "");

      if (numericValue.length > 3 && numericValue.length <= 6) {
        value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
      } else if (numericValue.length > 6) {
        value = `(${numericValue.slice(0, 3)}) ${numericValue.slice(
          3,
          6
        )}-${numericValue.slice(6, 10)}`;
      }
    } else {
      value = value.replace(/[^a-zA-Z\d@.+]/g, "");
    }
    setInputValue(value);
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="email"
        control={control}
        defaultValue={inputValue}
        rules={{
          required: {
            value: true,
            message: countryCode === "USA"? t("account.phoneEmailRequired"): t("account.emailRequired"),
          },
          // if (countryCode === "AU" || hasAt) {
          //   const isValidEmail = /^\S+@\S+\.\S+$/.test(cleanValue);
          //   return isValidEmail ? true : t("account.emailValid");
          // } else {
          //   return t("account.invalidNumber");
          // }
          validate: (value) => {
            if (countryCode === "USA" && !value) {
              return t("account.phoneEmailRequired");
            }
            const cleanValue = value.trim();
            const hasAt = cleanValue.includes("@");
            const hasLetter = /[a-zA-Z]/.test(cleanValue || "");
            if (countryCode === "AU" || hasAt || hasLetter) {
              const isValidEmail = /^\S+@\S+\.\S+$/.test(cleanValue);
              return isValidEmail ? true : t("account.emailValid");
            } else {
              // const numericValue = cleanValue.replace(/[^\d]/g, "");
              if (
                value.replace(/\D/g, "").length === 10 ||
                value.replace(/\D/g, "").length === 11 ||
                value.length === 0
              ) {
                return true;
              } else {
                return t("account.phoneValid");
              }
              // return /^\d{10}$/.test(numericValue)
              //   ? true
              //   : t("account.phoneValid");
            }
          },
        }}
        render={(field) => (
          <TextField
            {...field}
            data-cy="email"
            variant="outlined"
            margin="normal"
            fullWidth
            id="email"
            label={
              countryCode === "USA"
                ? t("account.phoneEmail")
                : t("account.email")
            }
            name="email"
            autoComplete="email"
            error={!!errors.email}
            helperText={errors.email ? errors.email.message : ""}
            onChange={(e) => {
              handleInputChange(e);
              field?.onChange(e);
              trigger("email");
            }}
            value={inputValue}
            maxLength={10}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlinedIcon className={classes.inputIco} />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <TextField
        data-cy="password"
        variant="outlined"
        margin="normal"
        fullWidth
        id="password"
        label={t("account.password")}
        name="password"
        autoComplete="current-password"
        type={values.showPassword ? "text" : "password"}
        error={!!errors.password}
        helperText={errors.password ? errors.password.message : ""}
        inputRef={register({
          required: { value: true, message: t("account.passwordRequired") },
        })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon className={classes.inputIco} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                size="large"
              >
                {values.showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        style={{ paddingTop: 10 }}
      >
        <RouterLink
          to="forgot-password"
          variant="body2"
          style={{ color: "#76bc21", textDecoration: "none" }}
          disabled={submitting}
          data-cy="forgotPassword"
        >
          {t("account.passwordForgot")}
        </RouterLink>
      </Grid>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="column"
      >
        <Grid item>
          {submitting === false ? (
            <Grid item>
              <Grid item>
                <Button
                  type="submit"
                  size="medium"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={submitting}
                  style={{ width: 240, height: 50 }}
                  data-cy="signInButton"
                >
                  {t("account.signInButton")}
                </Button>
              </Grid>

              <Grid item style={{ textAlign: "center", margin: 25 }}>
                <RouterLink
                  style={{ color: "#6DA9B3", fontWeight: "bold" }}
                  to="/register"
                  data-cy="createAccountLink"
                >
                  {t("registration.createAccount")}
                </RouterLink>
              </Grid>

              {/* <Grid item style={{ textAlign: "center", margin: 25 }}>
                <RouterLink
                  style={{ color: "#6DA9B3", fontWeight: "bold" }}
                  to={`/signnow/${token}`}
                  data-cy="createAccountLink"
                >
                  Sign Now
                </RouterLink>
              </Grid>

              <Grid item style={{ textAlign: "center", margin: 25 }}>
                <RouterLink
                  style={{ color: "#6DA9B3", fontWeight: "bold" }}
                  // to="/idupload"
                  to={{ pathname: `/idupload/${token}` }}
                  data-cy="createIDUploadLink"
                >
                  {t("account.idUpload")}
                </RouterLink>
              </Grid>

              <Grid item style={{ textAlign: "center", margin: 25 }}>
                <RouterLink
                  style={{ color: "#6DA9B3", fontWeight: "bold" }}
                  to={`/autopay/${token}`}
                  data-cy="createAutoPay"
                >
                  Auto Pay
                </RouterLink>
              </Grid> */}
            </Grid>
          ) : (
            <div style={{ paddingTop: 20 }}>
              <TimeLoader />
            </div>
          )}
        </Grid>
        <Grid item>
          <div style={{ height: 162 }}></div>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          direction="row"
          style={{ paddingTop: 15, paddingBottom: 15 }}
        ></Grid>
      </Grid>
    </form>
  );
}

Login.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object.isRequired,
  userLoginSuccess: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    userLoginSuccess: state.userLoginSuccess,
  };
}

const mapDispatchToProps = {
  userLoginSuccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
